// Capa de acceso a datos sobre Firestore (frontend).
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "./firebase";
import type {
  Alumno,
  Apoderado,
  ConfigGeneral,
  Cuota,
  Curso,
  Gasto,
} from "./types";
import { calcularVencimiento, valorBaseCuota } from "./cuotas";

// Quita claves undefined (Firestore no las acepta; usar null si hace falta).
function clean<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) if (v !== undefined) out[k] = v;
  return out as T;
}

const nowISO = () => new Date().toISOString();
const genId = (prefix: string) =>
  `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

// ---- Config ----
export const DEFAULT_CONFIG: ConfigGeneral = {
  nombreAcademia: "Mi Academia",
  anoLectivo: new Date().getFullYear(),
  mesesActivos: [3, 4, 5, 6, 7, 8, 9, 10, 11],
  mesesDesfase: 1,
  interesMoraPct: 2,
  diasGracia: 5,
  descuentoHermanosPct: 10,
  emailAdmin: "admin@academia.cl",
  simboloMoneda: "$",
  moneda: "CLP",
  practicas: [
    { clave: "volleyball", nombre: "Vóleibol", valorCuota: 15000, diaVencimiento: 10, cuenta: "Banco Estado · Cta Cte 123456789" },
    { clave: "futbol", nombre: "Fútbol", valorCuota: 15000, diaVencimiento: 10, cuenta: "Banco Santander · Cta Cte 987654321" },
  ],
};

export async function getConfig(): Promise<ConfigGeneral> {
  const snap = await getDoc(doc(db, "config", "general"));
  if (!snap.exists()) return DEFAULT_CONFIG;
  return { ...DEFAULT_CONFIG, ...(snap.data() as ConfigGeneral) };
}

export async function saveConfig(cfg: ConfigGeneral): Promise<void> {
  await setDoc(doc(db, "config", "general"), clean({ ...cfg, actualizadoEn: nowISO() }));
}

// ---- Apoderados ----
export async function listApoderados(): Promise<Apoderado[]> {
  const snap = await getDocs(query(collection(db, "apoderados"), orderBy("nombre")));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Apoderado, "id">) }));
}
export async function saveApoderado(a: Apoderado): Promise<string> {
  const id = a.id || genId("APO");
  await setDoc(doc(db, "apoderados", id), clean({ ...a, id, creadoEn: a.creadoEn || nowISO() }));
  return id;
}
export async function deleteApoderado(id: string): Promise<void> {
  await deleteDoc(doc(db, "apoderados", id));
}

// ---- Alumnos ----
export async function listAlumnos(): Promise<Alumno[]> {
  const snap = await getDocs(query(collection(db, "alumnos"), orderBy("nombre")));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Alumno, "id">) }));
}
export async function listAlumnosByApoderado(apoderadoId: string): Promise<Alumno[]> {
  const snap = await getDocs(
    query(collection(db, "alumnos"), where("apoderadoId", "==", apoderadoId)),
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Alumno, "id">) }));
}
export async function saveAlumno(a: Alumno): Promise<string> {
  const id = a.id || genId("ALM");
  await setDoc(doc(db, "alumnos", id), clean({ ...a, id, creadoEn: a.creadoEn || nowISO() }));
  return id;
}
export async function deleteAlumno(id: string): Promise<void> {
  await deleteDoc(doc(db, "alumnos", id));
}

// ---- Cursos ----
export async function listCursos(): Promise<Curso[]> {
  const snap = await getDocs(query(collection(db, "cursos"), orderBy("nombre")));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Curso, "id">) }));
}
export async function saveCurso(c: Curso): Promise<string> {
  const id = c.id || genId("CUR");
  await setDoc(doc(db, "cursos", id), clean({ ...c, id }));
  return id;
}
export async function deleteCurso(id: string): Promise<void> {
  await deleteDoc(doc(db, "cursos", id));
}

// ---- Cuotas ----
export async function listCuotas(ano: number): Promise<Cuota[]> {
  const snap = await getDocs(query(collection(db, "cuotas"), where("ano", "==", ano)));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Cuota, "id">) }));
}
export async function listCuotasByApoderado(apoderadoId: string): Promise<Cuota[]> {
  const snap = await getDocs(
    query(collection(db, "cuotas"), where("apoderadoId", "==", apoderadoId)),
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Cuota, "id">) }));
}
export async function saveCuota(c: Cuota): Promise<string> {
  const id = c.id || genId("CUO");
  await setDoc(doc(db, "cuotas", id), clean({ ...c, id, actualizadoEn: nowISO() }));
  return id;
}

/**
 * Genera las cuotas de un mes para todos los alumnos activos (idempotente por alumno+practica+mes+ano).
 * Devuelve cuántas cuotas nuevas creó.
 */
export async function generarCuotasMes(
  mes: number,
  ano: number,
  cfg: ConfigGeneral,
  alumnos: Alumno[],
  _apoderados: Apoderado[],
  existentes: Cuota[],
): Promise<number> {
  const existe = new Set(existentes.map((c) => `${c.alumnoId}|${c.practica}|${c.mes}|${c.ano}`));
  // Cuenta de alumnos por apoderado para el descuento de hermanos.
  const porApoderado = new Map<string, number>();

  const batch = writeBatch(db);
  let creadas = 0;

  for (const al of alumnos.filter((a) => a.estado === "activo")) {
    const practicas: Array<"volleyball" | "futbol"> =
      al.practicas === "ambas" ? ["volleyball", "futbol"] : [al.practicas as "volleyball" | "futbol"];

    for (const practicaClave of practicas) {
      if (existe.has(`${al.id}|${practicaClave}|${mes}|${ano}`)) continue;
      const cfgP = cfg.practicas.find((p) => p.clave === practicaClave);
      if (!cfgP) continue;

      const montoBase = valorBaseCuota(al, cfgP);
      // Descuento hermanos: desde el 2º alumno del mismo apoderado.
      const idx = (porApoderado.get(al.apoderadoId) || 0) + 1;
      porApoderado.set(al.apoderadoId, idx);
      let descuento = 0;
      if (al.regimen === "exento") descuento = montoBase;
      else if (al.regimen === "beca") descuento = Math.round(montoBase * 0.5);
      else if (idx > 1) descuento = Math.round((montoBase * cfg.descuentoHermanosPct) / 100);

      const estado = al.regimen === "exento" ? "exento" : al.regimen === "beca" ? "beca" : "por_vencer";
      const montoFinal = Math.max(0, montoBase - descuento);
      const id = genId("CUO");
      const cuota: Cuota = {
        id,
        alumnoId: al.id,
        alumnoNombre: al.nombre,
        apoderadoId: al.apoderadoId,
        practica: practicaClave,
        mes,
        ano,
        montoBase,
        descuento,
        recargo: 0,
        montoFinal,
        cuenta: cfgP.cuenta,
        fechaVencimiento: calcularVencimiento(mes, ano, cfg.mesesDesfase, cfgP.diaVencimiento),
        fechaPago: null,
        estado,
        metodoPago: null,
        comprobante: null,
        observaciones: "",
        actualizadoEn: nowISO(),
      };
      batch.set(doc(db, "cuotas", id), clean(cuota as unknown as Record<string, unknown>));
      creadas++;
    }
  }
  if (creadas > 0) await batch.commit();
  return creadas;
}

// ---- Carga masiva de miembros ----
import type { ParsedAlumno } from "./import";

export interface ImportResumen {
  apoderadosNuevos: number;
  alumnosNuevos: number;
  cursosNuevos: number;
  omitidos: number;
}

/**
 * Importa filas parseadas: crea/reutiliza apoderados (dedupe por teléfono normalizado),
 * crea cursos por categoría (dedupe por nombre) y crea alumnos (vóleibol).
 * No duplica alumnos que ya existan con el mismo RUT.
 */
export async function importarMiembros(rows: ParsedAlumno[]): Promise<ImportResumen> {
  const [apoderadosExist, alumnosExist, cursosExist] = await Promise.all([
    listApoderados(),
    listAlumnos(),
    listCursos(),
  ]);

  const telKey = (t: string) => t.replace(/\s+/g, "");
  const apoByTel = new Map(apoderadosExist.filter((a) => a.telefono).map((a) => [telKey(a.telefono!), a]));
  const rutAlumnos = new Set(alumnosExist.map((a) => a.rut).filter(Boolean));
  const cursoByNombre = new Map(cursosExist.map((c) => [c.nombre.toUpperCase(), c]));

  const resumen: ImportResumen = { apoderadosNuevos: 0, alumnosNuevos: 0, cursosNuevos: 0, omitidos: 0 };
  const batch = writeBatch(db);

  for (const row of rows) {
    // Omite alumnos ya existentes (por RUT válido).
    if (row.rut && rutAlumnos.has(row.rut)) { resumen.omitidos++; continue; }

    // Apoderado (dedupe por teléfono).
    let apo = row.apoderadoTelefono ? apoByTel.get(telKey(row.apoderadoTelefono)) : undefined;
    if (!apo) {
      const id = genId("APO");
      apo = {
        id, nombre: row.apoderadoNombre || "Sin nombre", rut: "", email: "",
        telefono: row.apoderadoTelefono, estado: "activo", creadoEn: nowISO(),
      };
      batch.set(doc(db, "apoderados", id), clean(apo as unknown as Record<string, unknown>));
      if (row.apoderadoTelefono) apoByTel.set(telKey(row.apoderadoTelefono), apo);
      resumen.apoderadosNuevos++;
    }

    // Curso por categoría (dedupe por nombre).
    let curso = row.categoria ? cursoByNombre.get(row.categoria.toUpperCase()) : undefined;
    if (!curso && row.categoria) {
      const id = genId("CUR");
      curso = { id, nombre: row.categoria, nivel: row.categoria, profesor: "", cupos: 40, cuposOcupados: 0 };
      batch.set(doc(db, "cursos", id), clean(curso as unknown as Record<string, unknown>));
      cursoByNombre.set(row.categoria.toUpperCase(), curso);
      resumen.cursosNuevos++;
    }

    // Alumno.
    const almId = genId("ALM");
    const obs = row.categorias.length > 1 ? `Categorías: ${row.categorias.join(", ")}. ` : "";
    const alumno: Alumno = {
      id: almId, nombre: row.alumnoNombre, rut: row.rut, apoderadoId: apo.id, apoderadoNombre: apo.nombre,
      cursoId: curso?.id, cursoNombre: curso?.nombre, practicas: "volleyball", regimen: "normal",
      valorEspecial: null, fechaNacimiento: row.fechaNacimiento, fechaIngreso: nowISO().slice(0, 10),
      estado: "activo", observacion: obs + (row.rutValido ? "" : "⚠ RUT por verificar."), creadoEn: nowISO(),
    };
    batch.set(doc(db, "alumnos", almId), clean(alumno as unknown as Record<string, unknown>));
    if (row.rut) rutAlumnos.add(row.rut);
    resumen.alumnosNuevos++;
  }

  await batch.commit();
  return resumen;
}

// ---- Gastos ----
export async function listGastos(ano: number): Promise<Gasto[]> {
  const snap = await getDocs(query(collection(db, "gastos"), where("ano", "==", ano)));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Gasto, "id">) }));
}
export async function saveGasto(g: Gasto): Promise<string> {
  const id = g.id || genId("GAS");
  await setDoc(doc(db, "gastos", id), clean({ ...g, id, creadoEn: g.creadoEn || nowISO() }));
  return id;
}
export async function deleteGasto(id: string): Promise<void> {
  await deleteDoc(doc(db, "gastos", id));
}
